import classnames from 'classnames'
import styles from './ZoomImage.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import useStore from '@/store'
import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { Flip } from 'gsap/dist/Flip'
import { CustomEase } from 'gsap/dist/CustomEase'
import {
  DETAIL_IMAGE_BREAKPOINTS,
  DETAIL_IMAGE_ID,
} from '@/sections/ProductPageUi/ProductPageUiLeftSide/ProductPageUiLeftSide'
import useWindowResize from '@/hooks/use-window-resize'
import FocusLock from 'react-focus-lock'
import { focusElement } from '@/utils'
import LineButton from '@/components/LineButton/LineButton'

gsap.registerPlugin(Flip)
gsap.registerPlugin(CustomEase)

export const ZOOM_IMAGE_EASE = 'customEaseInOutZoomImage'
export const ZOOM_IMAGE_DURATION = 1.4

CustomEase.create(ZOOM_IMAGE_EASE, '.98,-0.01,.13,.99')

export const ZOOM_IMAGE_ID = 'zoomImageId-69'
export const ZOOM_IMAGE_BREAKPOINTS = {
  tablet: {
    width: 3000,
  },
  xs: {
    width: 1000,
  },
}

export const ZOOM_IMAGE_CLOSE_BUTTON_ID = 'zoomImageCloseButtonId-69'

const ZoomImage = ({ className }) => {
  const productPageData = useStore(state => state.productPageData)
  const unsubRef = useRef()
  const [enableRaf, setEnableRaf] = useState(false)
  const allowPanRef = useRef(false)
  const mouseCoordsRef = useRef({ x: 0, y: 0 })
  const calculationsRef = useRef({})
  const $imageContainerRef = useRef()
  const $closeRef = useRef()
  const zoomImageOpen = useStore(state => state.zoomImageOpen)
  const setZoomImageOpen = useStore(state => state.setZoomImageOpen)
  const panMultiplier = useRef(0)
  const resizeKey = useWindowResize()
  const [windowIsTaller, setWindowIsTaller] = useState(false)
  const setCursorState = useStore(state => state.setCursorState)
  const closeButtonAnimateInDebounceTimeoutRef = useRef(null)
  const hasAnimatedInRef = useRef(false)

  const animateImageToPosition = () => {
    hasAnimatedInRef.current = true
    const $elToFitFrom = document.getElementById(DETAIL_IMAGE_ID)

    if (!$elToFitFrom || !$imageContainerRef.current) return

    Flip.killFlipsOf([$imageContainerRef.current, $elToFitFrom])

    const state = Flip.getState($imageContainerRef.current)

    Flip.fit($imageContainerRef.current, $elToFitFrom, {
      scale: true,
    })

    Flip.to(state, {
      duration: ZOOM_IMAGE_DURATION,
      ease: ZOOM_IMAGE_EASE,
      scale: true,
      onComplete: () => {
        setCursorState('Close')
        setEnableRaf(true)
        allowPanRef.current = true
        gsap.to(panMultiplier, {
          current: 1,
          duration: 2,
        })
      },
    })
  }

  const reset = () => {
    allowPanRef.current = false
    setEnableRaf(false)
    setTimeout(() => {
      gsap.killTweensOf([$imageContainerRef.current, panMultiplier])
      panMultiplier.current = 0
      gsap.set($imageContainerRef.current, {
        y: 0,
        scale: 1,
        x: 0,
      })
    }, 10)
  }

  useEffect(() => {
    if (closeButtonAnimateInDebounceTimeoutRef.current) {
      clearTimeout(closeButtonAnimateInDebounceTimeoutRef.current)
    }

    if (zoomImageOpen) {
      animateImageToPosition()
      closeButtonAnimateInDebounceTimeoutRef.current = setTimeout(
        () => {
          if ($closeRef.current) {
            $closeRef.current.animateIn()
          }
        },
        ZOOM_IMAGE_DURATION * 1000 * 0.85,
      )
    } else {
      reset()
      if ($closeRef.current && hasAnimatedInRef.current) {
        $closeRef.current.animateOut()
      }
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomImageOpen])

  const runCalculations = () => {
    if (!$imageContainerRef.current) return

    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const imageContainerHeight = $imageContainerRef.current.offsetHeight
    const panDistance = (imageContainerHeight - windowHeight) * 0.5
    const windowIsTaller = windowHeight > imageContainerHeight

    calculationsRef.current = {
      windowHeight,
      windowWidth,
      windowIsTaller,
      imageContainerHeight,
      panDistance,
    }
  }

  useEffect(() => {
    runCalculations()

    const { windowHeight, imageContainerHeight, panDistance, windowWidth, windowIsTaller } = calculationsRef.current
    const yTo = gsap.quickTo($imageContainerRef.current, 'y', { duration: 0.6, ease: 'Power3.easeOut' })

    setWindowIsTaller(windowIsTaller)

    function raf() {
      if (!allowPanRef.current || windowIsTaller) return
      if (!windowHeight || !imageContainerHeight || !panDistance || !windowWidth) return
      const mouseYNormalized = ((mouseCoordsRef.current.y - windowHeight * 0.5) / windowHeight) * 2
      const toPan = panDistance * mouseYNormalized * -1 * panMultiplier.current
      yTo(toPan)
    }

    gsap.ticker.remove(raf)

    if (!enableRaf) return

    gsap.ticker.add(raf)

    return () => {
      gsap.ticker.remove(raf)
    }
  }, [productPageData, enableRaf, resizeKey])

  useEffect(() => {
    if (unsubRef.current) unsubRef.current()

    unsubRef.current = useStore.subscribe(
      state => [state.cursorPosition],
      value => {
        const mouseCoords = value[0]
        mouseCoordsRef.current = mouseCoords
      },
    )

    return () => {
      if (unsubRef.current) unsubRef.current()
    }
  }, [])

  const handleCloseClick = () => {
    if (!enableRaf) return
    allowPanRef.current = false
    setZoomImageOpen(false)
    setCursorState(null)
    setTimeout(() => {
      focusElement(document.getElementById(DETAIL_IMAGE_ID))
    }, 100)
  }

  if (!productPageData?.productData?.zoomImage) return null

  return (
    <div
      className={classnames(
        styles.ZoomImage,
        className,
        { [styles.zoomImageOpen]: zoomImageOpen },
        { [styles.windowIsTaller]: windowIsTaller },
      )}
    >
      <FocusLock disabled={!zoomImageOpen}>
        <LineButton
          tabIndex={zoomImageOpen ? 0 : -1}
          className={styles.close}
          ref={$closeRef}
          onClick={handleCloseClick}
          id={ZOOM_IMAGE_CLOSE_BUTTON_ID}
          label="Close"
          theme="dark"
          showInitially={false}
        />
      </FocusLock>
      <div className={styles.inner}>
        <div
          className={styles.imageContainer}
          ref={$imageContainerRef}
          id={ZOOM_IMAGE_ID}
          onClick={handleCloseClick}
          onMouseEnter={() => {
            if (!enableRaf) return
            setCursorState('Close')
          }}
          onMouseLeave={() => {
            setCursorState(null)
          }}
          aria-label="Close large image"
        >
          <SanityImage
            className={classnames(styles.image, styles.smallerImage)}
            image={productPageData?.productData?.zoomImage}
            breakpoints={DETAIL_IMAGE_BREAKPOINTS}
            disableLoadedOpacity
          />
          {zoomImageOpen && (
            <SanityImage
              className={classnames(styles.image, styles.fullImage)}
              image={productPageData?.productData?.zoomImage}
              breakpoints={ZOOM_IMAGE_BREAKPOINTS}
              disableLoadedOpacity
            />
          )}
        </div>
      </div>
    </div>
  )
}

ZoomImage.displayName = 'ZoomImage'

export default ZoomImage
